import { z } from 'zod';

/**
 * Условия партнёрки и вывода.
 *
 * ⚠️ НЕ УТВЕРЖДЕНО владельцем (docs/techplan.md → «Деньги»). Единственное место, где живут эти цифры.
 */
export const PARTNER_TERMS = {
  /** % с первой оплаты друга. */
  firstPercent: 50,
  /** % со всех следующих оплат. */
  nextPercent: 25,
  /** Начисление доступно к выводу через N дней (на случай возврата платежа). */
  holdDays: 7,
  /** Перевод на баланс: +N% сверху, сразу, без минимума. */
  toBalanceBonusPercent: 10,
  payout: {
    sbp: { min: 500, periodDays: 7, label: 'На карту по СБП', note: 'до 3 дней' },
    usdt: { min: 1000, periodDays: 7, label: 'USDT (TRC-20)', note: 'комиссия сети на тебе' },
  },
} as const;

export type PayoutMethod = keyof typeof PARTNER_TERMS.payout;
export const PAYOUT_METHOD_IDS = ['sbp', 'usdt'] as const satisfies readonly PayoutMethod[];

/** Комиссия партнёра с платежа, ₽ (целые, вниз). */
export function partnerCommission(amount: number, first: boolean): { percent: number; amount: number } {
  const percent = first ? PARTNER_TERMS.firstPercent : PARTNER_TERMS.nextPercent;
  return { percent, amount: Math.floor((amount * percent) / 100) };
}

/** Сколько придёт на баланс при переводе с партнёрского счёта. */
export function transferCredits(amount: number): number {
  return amount + Math.floor((amount * PARTNER_TERMS.toBalanceBonusPercent) / 100);
}

export const POSTBACK_MACROS = ['{event}', '{sub_id}', '{amount}', '{payout}', '{user_id}'] as const;

/** Постбэк: только https, публичный хост, без логина в URL. Проверка хоста по DNS — на сервере перед отправкой. */
export const postbackUrlSchema = z
  .string()
  .trim()
  .max(500)
  .refine((v) => {
    try {
      const u = new URL(v.replace(/\{[a-z_]+\}/g, 'x'));
      return u.protocol === 'https:' && !u.username && !u.password && u.hostname.includes('.');
    } catch {
      return false;
    }
  }, 'Нужен https-адрес');

/** Подставить макросы в шаблон постбэка. */
export function fillPostback(template: string, values: Record<'event' | 'sub_id' | 'amount' | 'payout' | 'user_id', string>): string {
  return template.replace(/\{(event|sub_id|amount|payout|user_id)\}/g, (_, k: keyof typeof values) => encodeURIComponent(values[k]));
}

const phone = z
  .string()
  .trim()
  .transform((v) => v.replace(/[^\d+]/g, ''))
  .refine((v) => /^(\+7|8|7)\d{10}$/.test(v), 'Телефон в формате +7XXXXXXXXXX')
  .transform((v) => `+7${v.slice(-10)}`);

export const payoutRequestSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('sbp'), amount: z.number().int().positive(), phone, bank: z.string().trim().min(2).max(60) }),
  z.object({
    method: z.literal('usdt'),
    amount: z.number().int().positive(),
    address: z.string().trim().regex(/^T[1-9A-HJ-NP-Za-km-z]{33}$/, 'Адрес TRC-20 начинается с T, 34 символа'),
  }),
]);
export type PayoutRequest = z.input<typeof payoutRequestSchema>;

export const transferRequestSchema = z.object({ amount: z.number().int().positive(), key: z.string().uuid() });

export const partnerPayoutSchema = z.object({
  id: z.string().uuid(),
  method: z.enum(PAYOUT_METHOD_IDS),
  amount: z.number().int(),
  status: z.enum(['pending', 'paid', 'rejected']),
  /** Куда: «+7 999 ***-**-12 · Тинькофф» / «TXy…9Ab». */
  destination: z.string(),
  note: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
});
export type PartnerPayout = z.infer<typeof partnerPayoutSchema>;

export const partnerSummarySchema = z.object({
  /** Ссылки: на бота и прямо в мини-апп. */
  link: z.string(),
  appLink: z.string().nullable(),
  refCode: z.string(),
  /** Пришло по ссылке / купили хотя бы раз. */
  invited: z.number().int(),
  bought: z.number().int(),
  /** Всего заработано (начисления минус сторно). */
  earned: z.number().int(),
  /** На счёте: всего, в холде, доступно к выводу. */
  balance: z.number().int(),
  held: z.number().int(),
  available: z.number().int(),
  /** Ближайшее начисление выйдет из холда. */
  nextReleaseAt: z.string().nullable(),
  lastPayoutAt: z.string().nullable(),
  /** Когда снова можно вывести на СБП / USDT (null — можно сейчас). */
  payoutAvailableAt: z.object({ sbp: z.string().nullable(), usdt: z.string().nullable() }),
  pendingPayout: partnerPayoutSchema.nullable(),
  payouts: z.array(partnerPayoutSchema),
  postbackUrl: z.string().nullable(),
  terms: z.object({
    firstPercent: z.number(),
    nextPercent: z.number(),
    holdDays: z.number(),
    toBalanceBonusPercent: z.number(),
    payout: z.object({ sbp: z.object({ min: z.number(), periodDays: z.number() }), usdt: z.object({ min: z.number(), periodDays: z.number() }) }),
  }),
});
export type PartnerSummary = z.infer<typeof partnerSummarySchema>;

/** Маска реквизитов для экрана и админки. */
export function maskDestination(method: PayoutMethod, d: { phone?: string; bank?: string; address?: string }): string {
  if (method === 'sbp') {
    const p = d.phone ?? '';
    return `${p.slice(0, 2)} ${p.slice(2, 5)} ***-**-${p.slice(-2)}${d.bank ? ` · ${d.bank}` : ''}`;
  }
  const a = d.address ?? '';
  return a.length > 10 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a;
}
